import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, Search, FileCheck2, Clock3, Receipt, CheckCircle2, Sparkles } from "lucide-react";
import { api } from "../api/client";
import { naira, formatDate } from "../data/format";

export default function CompletedPayments() {
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState("");
  const [fadingIds, setFadingIds] = useState({});
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api
      .completedPlans()
      .then((data) => {
        setPlans(data);
        setError("");
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function markGenerated(plan) {
    setFadingIds((f) => ({ ...f, [plan.id]: true }));
    try {
      await api.markReceiptGenerated(plan.id);
      setTimeout(() => {
        setPlans((prev) =>
          prev.map((p) => (p.id === plan.id ? { ...p, receipt_generated: true } : p))
        );
      }, 350);
    } catch (e) {
      setFadingIds((f) => ({ ...f, [plan.id]: false }));
      setError(e.message);
    }
  }

  const filtered = useMemo(() => {
    if (!plans) return null;
    const q = search.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter(
      (p) =>
        p.customer_name.toLowerCase().includes(q) ||
        p.equipment_name.toLowerCase().includes(q)
    );
  }, [plans, search]);

  const awaitingCount = plans ? plans.filter((p) => !p.receipt_generated).length : 0;
  const generatedCount = plans ? plans.length - awaitingCount : 0;
  const totalCount = plans ? plans.length : 0;
  const progressPct = totalCount ? Math.round((generatedCount / totalCount) * 100) : 0;

  const ELLIPSIS = "\u2026";
  const EMDASH = "\u2014";
  const MIDDOT = "\u00B7";

  if (error && !plans) return <div className="pl-empty">{error}</div>;
  if (!plans) return <div className="pl-empty">{"Loading" + ELLIPSIS}</div>;

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          width:"100%",
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            className="pl-page-title"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 26,
              letterSpacing: "-0.01em",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "linear-gradient(135deg,#eef2ff,#fdf2f8)",
                color: "#4f46e5",
                boxShadow: "0 1px 2px rgba(15,23,42,.06)",
                flexShrink: 0,
              }}
            >
              <Receipt size={18} />
            </span>
            Completed Payments
          </div>
          <div className="pl-page-sub" style={{ marginTop: 6, maxWidth: 620 }}>
            {"Plans that are fully paid off " + EMDASH + " mark each once its receipt has been generated."}
          </div>
        </div>

        {totalCount > 0 && (
          <div
            style={{
              minWidth: 220,
              flex: "1 1 220px",
              maxWidth: 320,
              padding: "12px 14px",
              borderRadius: 14,
              background: "#fff",
              border: "1px solid #eef0f4",
              boxShadow: "0 1px 2px rgba(15,23,42,.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
                marginBottom: 8,
                textTransform: "uppercase",
                letterSpacing: ".04em",
              }}
            >
              <span>Progress</span>
              <span style={{ color: "#0f172a" }}>{progressPct}%</span>
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 999,
                background: "#f1f5f9",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${progressPct}%`,
                  height: "100%",
                  background: "linear-gradient(90deg,#10b981,#22c55e)",
                  transition: "width .4s ease",
                }}
              />
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
              {generatedCount} of {totalCount} receipts generated
            </div>
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div
        className="pl-stat-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 14,
          maxWidth: 520,
          margin: "20px 0 24px",
        }}
      >
        <div
          className="pl-stat-card"
          style={{
            position: "relative",
            padding: 18,
            borderRadius: 16,
            background: "linear-gradient(180deg,#fffdf5,#ffffff)",
            border: "1px solid #fde68a55",
            boxShadow: "0 1px 2px rgba(15,23,42,.04)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "#fef3c7",
              color: "#b45309",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Clock3 size={16} />
          </div>
          <div
            className="pl-stat-value"
            style={{ color: "var(--amber-700,#b45309)", fontSize: 30, fontWeight: 700, lineHeight: 1 }}
          >
            {awaitingCount}
          </div>
          <div className="pl-stat-label" style={{ marginTop: 8, color: "#78716c", fontWeight: 600 }}>
            Awaiting receipt
          </div>
        </div>

        <div
          className="pl-stat-card"
          style={{
            position: "relative",
            padding: 18,
            borderRadius: 16,
            background: "linear-gradient(180deg,#f0fdf4,#ffffff)",
            border: "1px solid #bbf7d055",
            boxShadow: "0 1px 2px rgba(15,23,42,.04)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              borderRadius: 10,
              background: "#dcfce7",
              color: "#15803d",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircle2 size={16} />
          </div>
          <div
            className="pl-stat-value"
            style={{ color: "var(--green-700,#15803d)", fontSize: 30, fontWeight: 700, lineHeight: 1 }}
          >
            {generatedCount}
          </div>
          <div className="pl-stat-label" style={{ marginTop: 8, color: "#64748b", fontWeight: 600 }}>
            Receipts generated
          </div>
        </div>
      </div>

      {/* Search */}
      <div
        className="pl-searchbar pl-completed-search"
        style={{
          position: "relative",
          marginBottom: 20,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "10px 12px 10px 36px",
          boxShadow: "0 1px 2px rgba(15,23,42,.04)",
        }}
      >
        <Search
          size={15}
          className="pl-search-icon"
          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
        />
        <input
          placeholder={"Search by customer or equipment" + ELLIPSIS}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 14,
            color: "#0f172a",
          }}
        />
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div
          className="pl-empty"
          style={{
            padding: "40px 20px",
            textAlign: "center",
            border: "1px dashed #e2e8f0",
            borderRadius: 14,
            background: "#fafafa",
            color: "#64748b",
          }}
        >
          <Sparkles size={22} style={{ color: "#94a3b8", marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: "#334155" }}>
            {search ? `No completed plan matches "${search}".` : "No completed plans yet."}
          </div>
          {search && (
            <div style={{ fontSize: 13, marginTop: 4 }}>Try a different customer or equipment name.</div>
          )}
        </div>
      )}

      {/* List */}
      <div
        className="pl-completed-list"
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        {filtered.map((plan) => {
          const fading = !!fadingIds[plan.id];
          const done = plan.receipt_generated;
          return (
            <div
              className="pl-completed-card"
              key={plan.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                rowGap: 10,
                flexWrap: "wrap",
                padding: "14px 16px",
                background: "#fff",
                border: "1px solid #eef0f4",
                borderLeft: `3px solid ${done ? "#22c55e" : "#f59e0b"}`,
                borderRadius: 14,
                boxShadow: "0 1px 2px rgba(15,23,42,.04)",
                transition: "transform .15s ease, box-shadow .15s ease, opacity .3s ease",
                opacity: fading ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(15,23,42,.06)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 2px rgba(15,23,42,.04)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div
                className="pl-completed-card-main"
                style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0, flex: "1 1 240px" }}
              >
                <div
                  className="pl-completed-avatar"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: done
                      ? "linear-gradient(135deg,#dcfce7,#bbf7d0)"
                      : "linear-gradient(135deg,#eef2ff,#e0e7ff)",
                    color: done ? "#15803d" : "#4f46e5",
                    flexShrink: 0,
                  }}
                >
                  <FileCheck2 size={20} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Link
                    to={`/customers/${plan.customer}`}
                    className="pl-completed-name"
                    style={{
                      fontWeight: 600,
                      color: "#0f172a",
                      textDecoration: "none",
                      fontSize: 15,
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {plan.customer_name}
                  </Link>
                  <div
                    className="pl-completed-meta"
                    style={{ fontSize: 13, color: "#475569", marginTop: 2 }}
                  >
                    <span style={{ fontWeight: 500 }}>{plan.equipment_name}</span>
                    <span style={{ margin: "0 8px", color: "#cbd5e1" }}>{MIDDOT}</span>
                    <span style={{ color: "#0f172a", fontWeight: 600 }}>
                      {naira(plan.total_price)}
                    </span>
                    <span style={{ color: "#94a3b8" }}> total</span>
                  </div>
                  <div
                    className="pl-completed-meta pl-completed-date"
                    style={{
                      fontSize: 12,
                      color: "#94a3b8",
                      marginTop: 4,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Clock3 size={12} />
                    {"Completed " + (plan.completed_at ? formatDate(plan.completed_at) : EMDASH)}
                  </div>
                </div>
              </div>

              <div
                className="pl-completed-card-status"
                style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 220px", justifyContent: "flex-end" }}
              >
                {done ? (
                  <span
                    className="pl-chip pl-chip-green"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#15803d",
                      background: "#dcfce7",
                      border: "1px solid #bbf7d0",
                      borderRadius: 999,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <CheckCircle2 size={12} /> Receipt generated
                  </span>
                ) : (
                  <span
                    className="pl-chip pl-chip-amber"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#b45309",
                      background: "#fef3c7",
                      border: "1px solid #fde68a",
                      borderRadius: 999,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Clock3 size={12} /> Awaiting receipt generation
                  </span>
                )}

                {!done && (
                  <button
                    className="pl-tick-btn"
                    style={{
                      width: 36,
                      height: 36,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "linear-gradient(135deg,#22c55e,#16a34a)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 10,
                      cursor: "pointer",
                      flexShrink: 0,
                      boxShadow: "0 2px 6px rgba(22,163,74,.35)",
                      opacity: fading ? 0 : 1,
                      pointerEvents: fading ? "none" : "auto",
                      transition: "transform .1s ease, opacity .3s ease, box-shadow .15s",
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.transform = "scale(.94)")}
                    onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    onClick={() => markGenerated(plan)}
                    aria-label="Mark receipt generated"
                    title="Mark receipt generated"
                  >
                    <Check size={18} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
